import React, { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  MarkerType,
} from '@xyflow/react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { EquipmentNode, EquipData, SideHandles } from './EquipmentShapes';
import { NodeProps } from '@xyflow/react';
import { OlefinRoute } from '../../types';
import { installPidLint } from './pidLint';
import { installPidHops } from './pidHop';
import { measureLaneTargets } from './pidAlign';

/**
 * CCR（连续重整）装置 P&ID 风格详细流程图
 * 4 台加热炉 + 4 台反应器串联、循环氢系统、催化剂连续再生、脱戊烷塔分馏、公用工程
 */

export type Medium = 'oil' | 'h2' | 'cat' | 'water' | 'steam' | 'fuel' | 'air';

const MEDIUM_META: Record<
  Medium,
  { label: string; light: string; dark: string; width: number; dash?: string }
> = {
  oil: { label: '石脑油 / 油品', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  h2: { label: '氢气 / 循环氢', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  cat: { label: '催化剂（连续输送）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  water: { label: '水 / 凝结水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  fuel: { label: '燃料气', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
  air: { label: '空气', light: '#00838f', dark: '#4dd0e1', width: 2 },
};

interface PortData extends Record<string, unknown> {
  label: string;
  color: string;
  direction: 'in' | 'out';
}

const PortNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as PortData;
  return (
    <div
      className="pid-label"
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: d.color,
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        position: 'relative',
      }}
    >
      {d.direction === 'in' ? '▶' : '◀'} {d.label}
      <SideHandles />
    </div>
  );
};

const nodeTypes = { equip: EquipmentNode, port: PortNode };

interface Dev {
  id: string;
  kind: EquipmentKindAlias;
  x: number;
  y: number;
  tag: string;
  name: string;
  note?: string;
}
type EquipmentKindAlias = EquipData['kind'];

interface Pipe {
  id: string;
  from: string;
  fromSide: 'top' | 'bottom' | 'left' | 'right';
  to: string;
  toSide: 'top' | 'bottom' | 'left' | 'right';
  medium: Medium;
  label?: string;
}

const DEVICES: Dev[] = [
  // 主线设备 handle 中线全部对齐 y=233（物料主线一条贯通直线）；塔顶走廊 y=117
  { id: 'v101', kind: 'hDrum', x: 110, y: 205, tag: 'V-101', name: '石脑油进料缓冲罐' },
  { id: 'p101', kind: 'pump', x: 270, y: 209, tag: 'P-101', name: '进料泵' },
  { id: 'e101', kind: 'exchanger', x: 390, y: 204, tag: 'E-101', name: '进料换热器', note: '与生成油换热' },
  { id: 'mix101', kind: 'mixer', x: 520, y: 225, tag: '', name: '' },
  { id: 'f101', kind: 'furnace', x: 580, y: 169, tag: 'F-101', name: '一段加热炉', note: '出口 528℃' },
  { id: 'r101', kind: 'reactor', x: 720, y: 151, tag: 'R-101', name: '重整一反', note: '出口 ~448℃' },
  { id: 'f102', kind: 'furnace', x: 830, y: 169, tag: 'F-102', name: '二段加热炉', note: '出口 509℃' },
  { id: 'r102', kind: 'reactor', x: 970, y: 151, tag: 'R-102', name: '重整二反', note: '出口 ~468℃' },
  { id: 'f103', kind: 'furnace', x: 1080, y: 169, tag: 'F-103', name: '三段加热炉', note: '出口 497℃' },
  { id: 'r103', kind: 'reactor', x: 1220, y: 151, tag: 'R-103', name: '重整三反', note: '出口 ~476℃' },
  { id: 'f104', kind: 'furnace', x: 1330, y: 169, tag: 'F-104', name: '四段加热炉', note: '出口 492℃' },
  { id: 'r104', kind: 'reactor', x: 1470, y: 151, tag: 'R-104', name: '重整四反', note: '出口 ~478℃' },
  { id: 'e102', kind: 'exchanger', x: 1600, y: 204, tag: 'E-102', name: '生成油水冷器', note: '40℃' },
  { id: 'v105', kind: 'vDrum', x: 1710, y: 177, tag: 'V-105', name: '高压分离器', note: '40℃ / 1.5MPa' },
  { id: 't201', kind: 'column', x: 1870, y: 117, tag: 'T-201', name: '脱戊烷塔' },
  { id: 'e203', kind: 'exchanger', x: 2010, y: 88, tag: 'E-203', name: '塔顶冷凝器' },
  { id: 'v207', kind: 'hDrum', x: 2120, y: 89, tag: 'V-207', name: '塔顶回流罐' },
  { id: 'p207', kind: 'pump', x: 2290, y: 93, tag: 'P-207', name: '回流泵' },
  { id: 'e202', kind: 'exchanger', x: 1960, y: 320, tag: 'E-202', name: '塔底重沸器' },
  // 循环氢压缩机：位于 R-103/F-104 之间通道（x=1323 为 F-104 燃料气竖线走廊，压缩机须避开）
  { id: 'c101', kind: 'compressor', x: 1225, y: 430, tag: 'C-101', name: '循环氢压缩机' },
  { id: 'd201', kind: 'vDrum', x: 1660, y: 560, tag: 'D-201', name: '待生剂收集斗' },
  { id: 'rg201', kind: 'regenerator', x: 430, y: 480, tag: 'RG-201', name: '催化剂再生器' },
];

interface Port {
  id: string;
  label: string;
  medium: Medium;
  x: number;
  y: number;
  direction: 'in' | 'out';
}

const PORTS: Port[] = [
  { id: 'p-feed', label: '石脑油原料', medium: 'oil', x: 0, y: 224, direction: 'in' },
  { id: 'p-h2', label: '氢气产品', medium: 'h2', x: 1390, y: 448, direction: 'out' },
  { id: 'p-lpg', label: '液化气', medium: 'oil', x: 2390, y: 108, direction: 'out' },
  { id: 'p-gas', label: '重整汽油 / 芳烃', medium: 'oil', x: 2170, y: 224, direction: 'out' },
  { id: 'p-air', label: '再生空气', medium: 'air', x: 330, y: 500, direction: 'in' },
  // 燃料气汇管放到再生器下方（y≈813），横干道避开 RG-201 本体与标签
  { id: 'p-fg', label: '燃料气', medium: 'fuel', x: 400, y: 800, direction: 'in' },
  { id: 'p-stm', label: '1.0MPa 蒸汽', medium: 'steam', x: 1830, y: 430, direction: 'in' },
  { id: 'p-cond', label: '凝结水', medium: 'water', x: 2110, y: 450, direction: 'out' },
  { id: 'p-cw', label: '循环水', medium: 'water', x: 1620, y: 350, direction: 'in' },
];

const PIPES: Pipe[] = [
  // ── 油品物料主线 ──
  { id: 'e01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 'e02', from: 'v101', fromSide: 'right', to: 'p101', toSide: 'left', medium: 'oil' },
  { id: 'e03', from: 'p101', fromSide: 'right', to: 'e101', toSide: 'left', medium: 'oil' },
  { id: 'e04', from: 'e101', fromSide: 'right', to: 'mix101', toSide: 'left', medium: 'oil' },
  { id: 'e05', from: 'mix101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'oil', label: '混氢油' },
  { id: 'e06', from: 'f101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'oil' },
  { id: 'e07', from: 'r101', fromSide: 'right', to: 'f102', toSide: 'left', medium: 'oil' },
  { id: 'e08', from: 'f102', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'oil' },
  { id: 'e09', from: 'r102', fromSide: 'right', to: 'f103', toSide: 'left', medium: 'oil' },
  { id: 'e10', from: 'f103', fromSide: 'right', to: 'r103', toSide: 'left', medium: 'oil' },
  { id: 'e11', from: 'r103', fromSide: 'right', to: 'f104', toSide: 'left', medium: 'oil' },
  { id: 'e12', from: 'f104', fromSide: 'right', to: 'r104', toSide: 'left', medium: 'oil' },
  { id: 'e13', from: 'r104', fromSide: 'right', to: 'e102', toSide: 'left', medium: 'oil', label: '生成油' },
  { id: 'e14', from: 'e102', fromSide: 'right', to: 'v105', toSide: 'left', medium: 'oil' },
  { id: 'e15', from: 'v105', fromSide: 'bottom', to: 't201', toSide: 'left', medium: 'oil' },
  { id: 'e16', from: 't201', fromSide: 'bottom', to: 'e202', toSide: 'left', medium: 'oil' },
  { id: 'e17', from: 'e202', fromSide: 'top', to: 't201', toSide: 'right', medium: 'oil' },
  { id: 'e18', from: 't201', fromSide: 'right', to: 'p-gas', toSide: 'left', medium: 'oil', label: '重整汽油' },
  { id: 'e19', from: 't201', fromSide: 'right', to: 'e203', toSide: 'left', medium: 'oil' },
  { id: 'e20', from: 'e203', fromSide: 'right', to: 'v207', toSide: 'left', medium: 'oil' },
  { id: 'e21', from: 'v207', fromSide: 'right', to: 'p207', toSide: 'top', medium: 'oil' },
  { id: 'e22', from: 'p207', fromSide: 'bottom', to: 't201', toSide: 'right', medium: 'oil' },
  { id: 'e23', from: 'p207', fromSide: 'right', to: 'p-lpg', toSide: 'left', medium: 'oil', label: '液化气' },
  // ── 氢气系统 ──
  { id: 'e24', from: 'v105', fromSide: 'right', to: 'c101', toSide: 'top', medium: 'h2' },
  { id: 'e25', from: 'c101', fromSide: 'top', to: 'mix101', toSide: 'left', medium: 'h2' },
  { id: 'e26', from: 'c101', fromSide: 'right', to: 'p-h2', toSide: 'left', medium: 'h2' },
  // ── 催化剂连续再生（CCR）：待生剂沿顶部走廊逐级流向四反，再入收集斗与再生器 ──
  { id: 'e27', from: 'r101', fromSide: 'top', to: 'r102', toSide: 'top', medium: 'cat' },
  { id: 'e28', from: 'r102', fromSide: 'top', to: 'r103', toSide: 'top', medium: 'cat' },
  { id: 'e29', from: 'r103', fromSide: 'top', to: 'r104', toSide: 'top', medium: 'cat' },
  { id: 'e30', from: 'r104', fromSide: 'top', to: 'd201', toSide: 'top', medium: 'cat' },
  { id: 'e31', from: 'd201', fromSide: 'left', to: 'rg201', toSide: 'right', medium: 'cat' },
  { id: 'e32', from: 'rg201', fromSide: 'top', to: 'r101', toSide: 'top', medium: 'cat' },
  // ── 公用工程 ──
  { id: 'e33', from: 'p-cw', fromSide: 'right', to: 'e102', toSide: 'right', medium: 'water' },
  { id: 'e34', from: 'p-stm', fromSide: 'right', to: 'e202', toSide: 'bottom', medium: 'steam' },
  { id: 'e35', from: 'e202', fromSide: 'right', to: 'p-cond', toSide: 'left', medium: 'water' },
  { id: 'e36', from: 'p-fg', fromSide: 'right', to: 'f101', toSide: 'bottom', medium: 'fuel' },
  { id: 'e37', from: 'p-fg', fromSide: 'right', to: 'f102', toSide: 'bottom', medium: 'fuel' },
  { id: 'e38', from: 'p-fg', fromSide: 'right', to: 'f103', toSide: 'bottom', medium: 'fuel' },
  { id: 'e39', from: 'p-fg', fromSide: 'right', to: 'f104', toSide: 'bottom', medium: 'fuel' },
  { id: 'e43', from: 'p-air', fromSide: 'right', to: 'rg201', toSide: 'left', medium: 'air' },
];

interface FlowDiagramProps {
  route: OlefinRoute;
}

const CCRInner: React.FC<FlowDiagramProps> = ({ route }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const rfInstance = useReactFlow();
  // 车道吸附：目标 y 写回数据源（受控模式下 setNodes 会被 props 回滚）
  const [snappedY, setSnappedY] = useState<Record<string, number>>({});

  useEffect(() => {
    installPidLint();
    installPidHops();
  }, []);

  useEffect(() => {
    const apply = () => {
      try {
        const t = measureLaneTargets(rfInstance);
        if (Object.keys(t).length > 0) setSnappedY((prev) => ({ ...prev, ...t }));
      } catch {
        /* 单次失败由后续调度补 */
      }
    };
    const t1 = setTimeout(apply, 120);
    const t2 = setTimeout(apply, 900);
    const doc = document as Document & { fonts?: { ready: Promise<unknown> } };
    let t3: ReturnType<typeof setTimeout> | undefined;
    doc.fonts?.ready.then(() => {
      t3 = setTimeout(apply, 60);
    });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (t3) clearTimeout(t3);
    };
  }, [rfInstance]);

  const { nodes, edges } = useMemo(() => {
    const ns: Node[] = [
      ...DEVICES.map<Node>((d) => ({
        id: d.id,
        type: 'equip',
        position: { x: d.x, y: snappedY[d.id] ?? d.y },
        data: { kind: d.kind, tag: d.tag, name: d.name, note: d.note, dark: isDark } as EquipData,
        draggable: true,
      })),
      ...PORTS.map<Node>((p) => ({
        id: p.id,
        type: 'port',
        position: { x: p.x, y: snappedY[p.id] ?? p.y },
        data: {
          label: p.label,
          color: MEDIUM_META[p.medium][isDark ? 'dark' : 'light'],
          direction: p.direction,
        } as PortData,
        draggable: true,
      })),
    ];
    const es: Edge[] = PIPES.map((p) => {
      const m = MEDIUM_META[p.medium];
      const color = m[isDark ? 'dark' : 'light'];
      return {
        id: p.id,
        source: p.from,
        sourceHandle: `${p.fromSide}-s`,
        target: p.to,
        targetHandle: `${p.toSide}-t`,
        type: 'smoothstep' as const,
        animated: p.medium === 'cat',
        label: p.label,
        labelStyle: { fill: isDark ? '#eceff1' : '#37474f', fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: isDark ? '#1e1e1e' : '#ffffff' },
        style: { stroke: color, strokeWidth: m.width, strokeDasharray: m.dash },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      };
    });
    return { nodes: ns, edges: es };
  }, [isDark, snappedY]);

  return (
    <Card sx={{ backgroundColor: isDark ? '#1e1e1e' : '#ffffff' }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}>
          {route.name} · CCR 连续重整装置详细流程
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          4 台加热炉与 4 台反应器串联（段间补热）· 循环氢系统 · 催化剂连续再生（烧焦 / 氯化更新 / 干燥）· 脱戊烷塔分馏 · 公用工程
          （设备形状按 P&ID 惯例绘制，管线按介质分色，可拖动 / 缩放查看细节）
        </Typography>
        <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">原料</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.feedstock.map((item) => (
                <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">产品 / 副产</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {[...route.products, ...(route.byproducts ?? [])].map((item) => (
                <Chip key={item} label={item} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 1.5, borderColor: isDark ? '#444444' : '#e0e0e0' }} />
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {(Object.keys(MEDIUM_META) as Medium[]).map((k) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 0,
                  borderTopWidth: MEDIUM_META[k].dash ? 2 : 3,
                  borderTopStyle: MEDIUM_META[k].dash ? 'dashed' : 'solid',
                  borderTopColor: MEDIUM_META[k][isDark ? 'dark' : 'light'],
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                {MEDIUM_META[k].label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            height: 640,
            border: '1px solid',
            borderColor: isDark ? '#444444' : '#e0e0e0',
            borderRadius: 2,
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.08, maxZoom: 0.6 }}
            minZoom={0.05}
            maxZoom={2}
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={isDark ? '#333333' : '#e0e0e0'}
            />
          </ReactFlow>
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {[
            '竖长容器 = 反应器（内虚线为催化剂床层）',
            '方箱 + 烟囱 = 加热炉（对角线为辐射段）',
            '细长带塔盘 = 分馏塔',
            '椭圆封头容器 = 缓冲罐 / 分离器 / 回流罐',
            '圆形 = 换热器',
            '圆 + 三角 = 泵',
            '梯形 = 压缩机',
            '分段立式容器 = 催化剂再生器',
          ].map((t) => (
            <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 10 }} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const CCRDiagram: React.FC<FlowDiagramProps> = (props) => (
  <ReactFlowProvider>
    <CCRInner {...props} />
  </ReactFlowProvider>
);

export default CCRDiagram;
